// src/pages/qc/PageQCHmSpecs.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";

function fmt(min, max, unit) {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${min} – ${max}${unit ? " " + unit : ""}`;
  if (max != null) return `≤ ${max}${unit ? " " + unit : ""}`;
  return `≥ ${min}${unit ? " " + unit : ""}`;
}

function viscText(s) {
  if (!s.viscosityMin && !s.viscosityMax) return "—";
  const range = fmt(s.viscosityMin, s.viscosityMax, "cps");
  return s.viscosityTempC ? `${range} @ ${s.viscosityTempC}°C` : range;
}

export function PageQCHmSpecs({ hms }) {
  const [specs,          setSpecs]          = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [search,         setSearch]         = useState("");
  const [hovCol,         setHovCol]         = useState(null);

  useEffect(() => {
    Promise.all([api.fetchHmSpecs(), api.fetchHmGrades()])
      .then(([hmSpecs, grades]) => {
        setSpecs(hmSpecs);
        const m = {};
        grades.forEach(g => { m[g.id] = g.displayName || g.id; });
        setDisplayNameMap(m);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return specs.filter(s =>
      s.hmId.toLowerCase().includes(q) ||
      (displayNameMap[s.hmId] || "").toLowerCase().includes(q)
    );
  }, [specs, search, displayNameMap]);

  // Determine which columns have any data (to avoid empty columns for PSA vs HM)
  const hasSetOpen  = specs.some(s => s.setTimeMin != null || s.setTimeMax != null);
  const hasPeelTack = specs.some(s => s.peelAdhesionMin != null || s.loopTackMin != null);

  const headers = [
    "Grade", "Appearance", "Odor",
    "Viscosity (cps)", "Softening Pt (°C)",
    ...(hasSetOpen  ? ["Set Time (s)", "Open Time (s)"] : []),
    ...(hasPeelTack ? ["Peel Adhesion (kg/in)", "Loop Tack (kg/in²)"] : []),
  ];

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Hotmelt Specifications</div>
          <div className="page-subtitle">Hotmelt &amp; PSA adhesive quality parameters</div>
        </div>
      </div>

      <div className="action-bar">
        <input className="form-input" placeholder="Search grade…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:220 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table" data-hov-col={hovCol ?? undefined} onMouseLeave={() => setHovCol(null)}>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} onMouseEnter={() => setHovCol(i)}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, ri) => {
                const isPSA = s.peelAdhesionMin != null || s.loopTackMin != null;
                return (
                  <tr key={s.hmId}>
                    <td onMouseEnter={() => setHovCol(0)} className="fw-600">
                      {displayNameMap[s.hmId] || s.hmId}
                      {isPSA
                        ? <span className="badge badge-purple" style={{ marginLeft:6 }}>PSA</span>
                        : <span className="badge badge-green"  style={{ marginLeft:6 }}>HM</span>
                      }
                    </td>
                    <td onMouseEnter={() => setHovCol(1)}>{s.appearance ?? "—"}</td>
                    <td onMouseEnter={() => setHovCol(2)}>{s.odor ?? "—"}</td>
                    <td onMouseEnter={() => setHovCol(3)}>{viscText(s)}</td>
                    <td onMouseEnter={() => setHovCol(4)}>{fmt(s.softeningPtMin, s.softeningPtMax, "°C")}</td>
                    {hasSetOpen && <>
                      <td onMouseEnter={() => setHovCol(5)}>{fmt(s.setTimeMin,  s.setTimeMax)}</td>
                      <td onMouseEnter={() => setHovCol(6)}>{fmt(s.openTimeMin, s.openTimeMax)}</td>
                    </>}
                    {hasPeelTack && <>
                      <td onMouseEnter={() => setHovCol(hasSetOpen ? 7 : 5)}>{fmt(s.peelAdhesionMin, s.peelAdhesionMax)}</td>
                      <td onMouseEnter={() => setHovCol(hasSetOpen ? 8 : 6)}>{fmt(s.loopTackMin,     s.loopTackMax)}</td>
                    </>}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={headers.length} style={{ textAlign:"center", color:"#aaa", padding:24 }}>No specs found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
